import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { COLORS, LAYOUT } from '../styles/tokens';

/** Sidebar nav icon definition */
interface SidebarIcon {
  path: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

/** Home icon */
function HomeIcon(): React.JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

/** Wrench/tools icon */
function ToolsIcon(): React.JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

/** Settings gear icon */
function GearIcon(): React.JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

/** Users icon */
function UsersIcon(): React.JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

const SIDEBAR_ICONS: SidebarIcon[] = [
  { path: '/', label: 'Home', icon: <HomeIcon /> },
  { path: '/apps', label: 'Apps', icon: <ToolsIcon />, adminOnly: true },
  { path: '/users', label: 'User Management', icon: <UsersIcon />, adminOnly: true },
  { path: '/admin', label: 'Admin', icon: <GearIcon />, adminOnly: true },
];

interface NarrowSidebarProps {
  /** When true the off-canvas drawer is visible on small screens. */
  mobileOpen?: boolean;
  /** Fired after a nav icon is chosen so the mobile drawer can close. */
  onNavigate?: () => void;
}

/** 64px narrow icon-only sidebar */
export function NarrowSidebar({ mobileOpen = false, onNavigate }: NarrowSidebarProps): React.JSX.Element {
  const { isDark } = useTheme();
  const { roles } = useAuth();
  const isAdmin = roles.includes('admin');
  const colors = isDark ? COLORS.dark : COLORS.light;

  const visibleItems = SIDEBAR_ICONS.filter(item => !item.adminOnly || isAdmin);

  return (
    <aside
      className={`vx-sidebar${mobileOpen ? ' is-open' : ''}`}
      style={{
        width: `${LAYOUT.sidebarWidth}px`,
        height: '100vh',
        backgroundColor: colors.card,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        transition: 'background-color 0.3s, border-color 0.3s',
        paddingTop: '12px',
        gap: '4px',
        zIndex: 10,
      }}
    >
      {/* Vertex stacked lockup. Two authored variants rather than one filtered
          image: the previous `invert(1) brightness(2)` only produced white
          because the old wordmark happened to be pure black, and it would
          corrupt any mark carrying color.

          The lockup is 396.68x370 units, so `contain` keeps it undistorted at
          52px wide and roughly 48px tall. That puts the VERTEX wordmark near
          9px of cap height, which is deliberate: the product name in the
          sidebar was preferred over the tighter chevron-only mark. */}
      <div
        className="vx-sidebar-brand"
        style={{
          width: '56px',
          height: '56px',
          display: 'grid',
          placeItems: 'center',
          marginBottom: '16px',
        }}
      >
        <img
          src={isDark ? '/brand/vertex-stacked-on-dark.svg' : '/brand/vertex-stacked-on-light.svg'}
          alt="Vertex"
          style={{ width: '52px', height: '52px', objectFit: 'contain' }}
        />
      </div>

      {/* Nav icons */}
      {visibleItems.map(item => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          title={item.label}
          onClick={() => onNavigate?.()}
          className="vx-sidebar-link"
          style={({ isActive }) => ({
            color: isActive ? (isDark ? '#01A9ED' : '#0165C6') : colors.textSecondary,
            backgroundColor: isActive
              ? (isDark ? 'rgba(1, 169, 237, 0.1)' : 'rgba(1, 101, 198, 0.08)')
              : 'transparent',
          })}
        >
          <span className="vx-sidebar-icon">{item.icon}</span>
          <span className="vx-sidebar-label">{item.label}</span>
        </NavLink>
      ))}
    </aside>
  );
}
